<template>
  <v-card>
    <v-container class="py-0">
      <v-row>
        <v-col cols="auto" class="pr-0">
          <v-badge overlap color="white" dark icon="terrain">
            <user-avatar :size="32" :user="goal.user"></user-avatar>
          </v-badge>
        </v-col>
        <v-col cols="auto" class="my-auto">
          <div class="secondarytext--text">
            {{ goal.user.fullName }}
          </div>
          <div class="caption lighttext--text">
            Created {{ goal.createdDate | longDate }}
          </div>
        </v-col>
        <v-spacer></v-spacer>
        <v-col
          cols="auto"
          :class="canEdit ? 'px-0' : 'pl-0'"
          v-if="!isComplete"
        >
          <v-select
            v-model="goal.statusId"
            :items="goal_statuses"
            item-text="name"
            item-value="id"
            solo
            height="30"
            hide-details
            :background-color="status_color"
            color="white"
            @change="updateGoal"
            :disabled="!canEdit"
            class="no-width"
          >
            <template v-slot:selection="{ item }">
              <v-icon color="white">{{
                getGoalStatusTypeIcon(item.id)
              }}</v-icon>
            </template>
          </v-select>
        </v-col>
        <v-col cols="auto" v-if="canEdit" class="px-0">
          <v-menu absolute>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon>
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list>
              <template v-if="!isComplete">
                <v-list-item @click="edit">
                  <v-list-item-icon>
                    <v-icon>edit</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteGoal">
                  <v-list-item-icon>
                    <v-icon>delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </template>
              <template v-else>
                <v-list-item @click="reopenGoal">
                  <v-list-item-icon>
                    <v-icon>lock_open</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Reopen</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <due-date-chip
            :dueDate="isComplete ? goal.completeDate : goal.dueDate"
            @click="canEdit ? edit : null"
            :completed="isComplete"
          ></due-date-chip>
        </v-col>
        <v-col
          cols="12"
          :class="
            'secondarytext--text pb-0 white-space-pre-line subtitle-1 ' +
              (isComplete ? 'text-decoration-line-through' : '')
          "
        >
          {{ goal.title }}
        </v-col>
        <v-col cols="12" v-if="goal.description" class="pt-0">
          <v-btn
            text
            color="info"
            small
            class="pa-0"
            @click="show_description = !show_description"
          >
            {{ show_description ? "Hide" : "Read" }} description
          </v-btn>
          <div v-if="show_description" class="subtitle-2 lighttext--text">
            {{ goal.description }}
          </div>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-btn
            v-if="goal.percentComplete == null"
            @click="trackPercentComplete"
            text
            color="secondarytext"
            :disabled="!canEdit"
          >
            <v-icon class="mr-2">add_circle</v-icon>
            <span>Add progress</span>
          </v-btn>
          <v-slider
            v-model="goal.percentComplete"
            color="secondarytext"
            step="5"
            thumb-size="50"
            track-color="background"
            loader-height="10"
            inverse-label
            :label="goal.percentComplete + '%'"
            hide-details
            @change="updateGoal"
            :readonly="!canEdit || isComplete"
            v-else
          ></v-slider>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-btn
            small
            text
            color="lighttext"
            v-if="(goal.notes || []).length > 0"
            class="pa-0 mr-2"
            @click="show_notes = !show_notes"
          >
            {{ (goal.notes || []).length }} Comments
            <v-icon small>
              {{ !show_notes ? "expand_more" : "expand_less" }}
            </v-icon>
          </v-btn>
          <v-btn
            small
            text
            color="lighttext"
            v-if="sorted_tasks.length > 0"
            class="pa-0 mr-2"
            @click="show_sub_goals = !show_sub_goals"
          >
            {{ sorted_tasks.length }} Sub Goals
            <v-icon small>
              {{ !show_sub_goals ? "expand_more" : "expand_less" }}
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <template v-if="show_notes">
      <template v-for="note in goal.notes">
        <note-line-item
          class="mx-0"
          :key="note.id"
          :note="note"
          v-on:edit="editNote(note)"
          deleteAction="goal/deleteGoalNote"
        >
        </note-line-item>
      </template>
    </template>
    <template v-if="show_sub_goals">
      <template v-for="task in sorted_tasks">
        <v-divider :key="task.id + 'divider'"></v-divider>
        <goal-task-line-item
          class="mx-0"
          :key="task.id"
          :goalTask="task"
          v-on:edit="editGoalTask(task)"
          :disabled="isComplete"
          :canEdit="canEdit"
        >
        </goal-task-line-item>
      </template>
    </template>
    <v-card-actions
      class="background rounded-b-lg pa-1"
      v-if="canEdit && !isComplete"
    >
      <v-menu absolute>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            color="lighttext"
            class="text--darken-1"
            large
          >
            <v-icon>add_circle</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="addGoalTask">
            <v-list-item-icon>
              <v-badge color="success" dot overlap>
                <v-icon>assignment_turned_in</v-icon>
              </v-badge>
            </v-list-item-icon>
            <v-list-item-title>Sub Goal</v-list-item-title>
          </v-list-item>
          <v-list-item @click="addNote">
            <v-list-item-icon>
              <v-badge color="noteprimary" dot overlap>
                <v-icon>message</v-icon>
              </v-badge>
            </v-list-item-icon>
            <v-list-item-title>Note</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer></v-spacer>
      <v-chip
        outlined
        label
        small
        color="info"
        class="light-background caption px-2 mr-1"
        v-if="
          goal.checkInDefinitionId &&
            !this.$route.path.includes(goal.checkInDefinitionId)
        "
        @click="goToRelatedMeeting(goal.checkInDefinitionId)"
      >
        <v-icon size="15" class="mr-2">link</v-icon>
        {{ goal.checkInDefinitionName }}
      </v-chip>
    </v-card-actions>
    <edit-note-dialog
      ref="editNote"
      saveAction="goal/saveGoalNote"
      label="goal"
      v-on:save="$emit('redraw')"
    ></edit-note-dialog>
    <edit-goal-dialog
      ref="editGoal"
      v-on:save="$emit('redraw')"
    ></edit-goal-dialog>
    <edit-goal-task-dialog
      ref="editGoalTask"
      v-on:save="$emit('redraw')"
    ></edit-goal-task-dialog>
  </v-card>
</template>

<script>
import NoteLineItem from "@/components/checkin/NoteLineItem";
import GoalTaskLineItem from "@/components/goals/GoalTaskLineItem";
import EditGoalDialog from "@/components/goals/EditGoalDialog";
import EditGoalTaskDialog from "@/components/goals/EditGoalTaskDialog";
import EditNoteDialog from "@/components/checkin/EditNoteDialog";
import DueDateChip from "@/components/checkin/DueDateChip";
import { mapGetters } from "vuex";
import goalStatusTypes from "@/helpers/mixins/goalStatusTypes";
export default {
  components: {
    EditGoalDialog,
    EditNoteDialog,
    NoteLineItem,
    DueDateChip,
    GoalTaskLineItem,
    EditGoalTaskDialog
  },
  mixins: [goalStatusTypes],
  name: "Goal",
  props: { goal: { type: Object }, canEdit: { type: Boolean } },
  data: () => ({
    show_notes: false,
    show_sub_goals: false,
    show_description: false
  }),
  watch: {
    show_notes(val, oldVal) {
      if (val != oldVal) {
        this.$emit("redraw");
      }
    },
    show_sub_goals(val, oldVal) {
      if (val != oldVal) {
        this.$emit("redraw");
      }
    },
    show_description(val, oldVal) {
      if (val != oldVal) {
        this.$emit("redraw");
      }
    }
  },
  computed: {
    sorted_tasks() {
      return this._.orderBy(
        this.goal.tasks,
        ["completeDate", "dueDate"],
        ["desc", "asc"]
      );
    },
    status_background_color() {
      return this.getColor(this.status_color, 0.05);
    },
    status_color() {
      return this.getGoalStatusTypeColor(this.goal.statusId);
    },
    isComplete() {
      return this.goal.completeDate != null;
    },
    ...mapGetters({
      goal_statuses: "lookup/goal_statuses",
      profile: "profile/profile"
    })
  },
  methods: {
    async goToRelatedMeeting(id) {
      if (
        await this.$root.$confirm(
          "Go to meeting",
          "Would you like to view the meeting space where this goal was created? This can help in understanding the reason behind the goal.",
          { color: "success" }
        )
      ) {
        this.$router.push({ path: `/meetings/${id}` });
      }
    },
    edit() {
      this.$refs.editGoal.showDialog(this.goal);
    },
    addNote() {
      this.$refs.editNote.showDialog({
        parentId: this.goal.id,
        text: "",
        userId: this.goal.user.id
      });
    },
    editNote(note) {
      this.$refs.editNote.showDialog(note);
    },
    addGoalTask() {
      this.$refs.editGoalTask.showDialog(
        { userId: this.goal.user.id, goalId: this.goal.id },
        this.goal.user
      );
    },
    editGoalTask(goalTask) {
      this.$refs.editGoalTask.showDialog(goalTask, this.goal.user);
    },
    trackPercentComplete() {
      this.goal.percentComplete = 0;
      this.updateGoal();
    },
    reopenGoal() {
      this.$store
        .dispatch("goal/reopenGoal", this.goal)
        .then(() => {
          this.$root.$snackbarSuccess("Goal reopened");
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while saving your goal."
          );
        });
    },
    updateGoal() {
      this.$store
        .dispatch("goal/saveGoal", this.goal)
        .then(() => {
          this.$emit("save");
          this.$root.$snackbarSuccess("Goal updated");
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while saving your goal."
          );
        });
    },
    async deleteGoal() {
      if (
        await this.$root.$confirm(
          "Delete goal",
          "Are you sure you want to delete this goal? Deleting this goal will remove all historical trends associated with this goal as well as all data within it.",
          { color: "red" }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch("goal/deleteGoal", this.goal)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Goal deleted");
            this.dialog = false;
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while deleting a goal."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-slider__track-container {
    height: 10px !important;
  }

  .v-slider__thumb {
    width: 20px;
    height: 20px;

    &::before {
      left: -8px;
      top: -8px;
    }
  }

  .v-select.v-text-field--outlined > .v-input__control > .v-input__slot {
    min-height: auto !important;

    .v-input__append-inner {
      margin-top: auto;
      margin-bottom: auto;
    }
  }
}

.text-truncate {
  max-width: 800px;
}

.v-card {
  border-top: solid 8px var(--v-background-base);
}

-deep .v-badge__badge {
  box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%),
    0 1px 5px 0 rgb(0 0 0 / 12%) !important;
}
   
   

.v-text-field.v-text-field--solo ::v-deep {
  .v-input__control {
    min-height: inherit !important;

    .v-input__slot {
      padding: 0 0 0 4px !important;

      .v-input__append-inner {
        padding-left: 0;

        i {
          color: var(--v-white-base) !important;
        }
      }
    }
  }
}
</style>
